import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { googleCalendarService, type CalendarListEntry } from '../services/googleCalendarService';
import { eventBus } from '../events/EventBus';
import './AddEventModal.css';

interface AddEventModalProps {
  /** Optional initial start time. Defaults to next 15-min slot. */
  initialStart?: Date;
  /** Optional initial end time. Defaults to start + 30min. */
  initialEnd?: Date;
  /** Optional initial all-day flag. Defaults to false. */
  initialAllDay?: boolean;
  onClose: () => void;
}

function roundUpToNext15(d: Date): Date {
  const next = new Date(d);
  next.setSeconds(0, 0);
  const m = next.getMinutes();
  const remainder = m % 15;
  if (remainder !== 0) next.setMinutes(m + (15 - remainder));
  return next;
}

function toLocalInputValue(d: Date, dateOnly = false): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = d.getFullYear();
  const mo = pad(d.getMonth() + 1);
  const da = pad(d.getDate());
  if (dateOnly) return `${y}-${mo}-${da}`;
  const h = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

function fromLocalInputValue(value: string, dateOnly = false): Date {
  // Browser native datetime-local / date inputs use local time, no timezone.
  // Constructing via the Date constructor's parser is unreliable across
  // browsers; build it explicitly so the result is always local.
  if (dateOnly) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart || '00:00').split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm);
}

export default function AddEventModal({
  initialStart,
  initialEnd,
  initialAllDay = false,
  onClose,
}: AddEventModalProps) {
  const defaultStart = useMemo(() => initialStart ?? roundUpToNext15(new Date()), [initialStart]);
  const defaultEnd = useMemo(() => {
    if (initialEnd) return initialEnd;
    const e = new Date(defaultStart);
    e.setMinutes(e.getMinutes() + 30);
    return e;
  }, [initialEnd, defaultStart]);

  const [title, setTitle] = useState('');
  const [allDay, setAllDay] = useState(initialAllDay);
  const [startInput, setStartInput] = useState(() => toLocalInputValue(defaultStart, initialAllDay));
  const [endInput, setEndInput] = useState(() => toLocalInputValue(defaultEnd, initialAllDay));
  const [description, setDescription] = useState('');
  const [calendars, setCalendars] = useState<CalendarListEntry[]>([]);
  const [calendarId, setCalendarId] = useState<string>('');
  const [loadingCalendars, setLoadingCalendars] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const status = await googleCalendarService.getConnectionStatus();
        if (cancelled) return;
        if (!status.connected) {
          setError('Google Calendar is not connected.');
          setLoadingCalendars(false);
          return;
        }
        const settings = googleCalendarService.getSettings();
        if (settings.syncDirection === 'read') setReadOnly(true);
        const list = await googleCalendarService.listCalendars();
        if (cancelled) return;
        // Only writable calendars are valid event-creation targets.
        const writable = list.filter((c) => c.accessRole === 'writer' || c.accessRole === 'owner');
        setCalendars(writable);
        const preferred = status.defaultCalendarId && writable.some((c) => c.id === status.defaultCalendarId)
          ? status.defaultCalendarId
          : (writable.find((c) => c.primary)?.id || writable[0]?.id || '');
        setCalendarId(preferred);
      } catch (err) {
        console.error('[AddEventModal] init failed:', err);
        if (!cancelled) setError('Failed to load calendars.');
      } finally {
        if (!cancelled) setLoadingCalendars(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  // When toggling all-day, re-format the inputs so the native picker shows
  // the right widget (date vs datetime-local).
  useEffect(() => {
    const startDate = fromLocalInputValue(startInput, !allDay);
    const endDate = fromLocalInputValue(endInput, !allDay);
    setStartInput(toLocalInputValue(startDate, allDay));
    setEndInput(toLocalInputValue(endDate, allDay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDay]);

  const handleSave = async () => {
    setError(null);
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!calendarId) {
      setError('No writable calendar available.');
      return;
    }

    const startDate = fromLocalInputValue(startInput, allDay);
    const endDate = fromLocalInputValue(endInput, allDay);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      setError('Invalid start or end time.');
      return;
    }
    if (endDate <= startDate) {
      setError('End must be after start.');
      return;
    }

    const payload: Parameters<typeof googleCalendarService.createEvent>[0] = {
      summary: title.trim(),
      start: allDay
        ? { date: toLocalInputValue(startDate, true) }
        : { dateTime: startDate.toISOString() },
      end: allDay
        ? { date: toLocalInputValue(endDate, true) }
        : { dateTime: endDate.toISOString() },
    };
    if (description.trim()) payload.description = description.trim();

    try {
      setIsSaving(true);
      await googleCalendarService.createEvent(payload, calendarId);
      eventBus.emit('calendar:event-changed', { calendarId });
      onClose();
    } catch (err: any) {
      const msg = err?.message || 'Failed to create event';
      setError(msg);
      if (/read-only/i.test(msg)) setReadOnly(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="add-event-modal-overlay" onClick={onClose}>
      <div className="add-event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-event-modal-header">
          <h3>New Event</h3>
          <button className="add-event-modal-close" onClick={onClose}>×</button>
        </div>

        {readOnly && (
          <div className="add-event-modal-warning">
            Calendar is in read-only mode.{' '}
            <Link to="/admin?tab=calendar" onClick={onClose}>Enable write</Link> to create events.
          </div>
        )}

        {error && <div className="add-event-modal-error">{error}</div>}

        <div className="add-event-modal-content">
          <div className="add-event-field">
            <label>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
              autoFocus
              disabled={isSaving}
            />
          </div>

          <div className="add-event-field-row">
            <label className="add-event-checkbox">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                disabled={isSaving}
              />
              <span>All day</span>
            </label>
          </div>

          <div className="add-event-field">
            <label>Start</label>
            <input
              type={allDay ? 'date' : 'datetime-local'}
              value={startInput}
              onChange={(e) => setStartInput(e.target.value)}
              onClick={(e) => { try { (e.currentTarget as HTMLInputElement).showPicker?.(); } catch {} }}
              disabled={isSaving}
            />
          </div>

          <div className="add-event-field">
            <label>End</label>
            <input
              type={allDay ? 'date' : 'datetime-local'}
              value={endInput}
              onChange={(e) => setEndInput(e.target.value)}
              onClick={(e) => { try { (e.currentTarget as HTMLInputElement).showPicker?.(); } catch {} }}
              disabled={isSaving}
            />
          </div>

          <div className="add-event-field">
            <label>Calendar</label>
            <select
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
              disabled={isSaving || loadingCalendars}
            >
              {loadingCalendars && <option value="">Loading…</option>}
              {!loadingCalendars && calendars.length === 0 && <option value="">No writable calendars</option>}
              {calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.summary}{c.primary ? ' (primary)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="add-event-field">
            <label>Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="add-event-modal-footer">
          <button onClick={onClose} disabled={isSaving} className="add-event-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || readOnly || loadingCalendars || !calendarId}
            className="add-event-save-btn"
          >
            {isSaving ? 'Saving…' : 'Create event'}
          </button>
        </div>
      </div>
    </div>
  );
}
